import { Download, Folder } from '@mui/icons-material';
import { Avatar, Dialog, DialogContent, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { DocumentUploadService } from '../../../Component/Api/DocumentUpload/DocumentUploadService';
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { DocumentDto, DocumentSearchRequest } from '../../../Component/Model/Dto/Document';
import { formatFileSize } from '../../../Component/Model/helpers';
import { getIconAndNameFromExtension } from '../../../Component/Model/iconHelper';
import config from '../../../config';
import { CustomDialogTitle } from '../Custom/CustomDialogTitle';

interface IProps {
    itemId: number;
    scorecardId: string;
    link: string;
    title: string;
    handleClose: () => void;
    showModal: boolean;
}

export const Attachments = (props: IProps) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [documentsUploaded, setDocumentsUploaded] = React.useState<DocumentDto[]>([]);

    const documentSearchRequest: DocumentSearchRequest = {
        id: props.itemId.toString(),
        link: props.link,
        scorecardId: props.scorecardId
    };

    const getDocumentsUploaded = async () => {
        setIsLoading(true);
        try {
            const results = await DocumentUploadService.GetDocumentsUploaded(documentSearchRequest);
            setDocumentsUploaded(results.data.DocumentsUploaded);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const getDocument = (id: number) => {
        const url = `${config.apiUrl}/DocumentUpload/Get?id=${id}&scorecardId=${props.scorecardId}`;
        window.open(url);
    }

    useEffect(() => {
        getDocumentsUploaded();
    }, [props.itemId]);

    return (
        <Dialog open={props.showModal} onClose={props.handleClose} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
            <CustomDialogTitle id="customized-dialog-title" onClose={props.handleClose}>
                {props.title}
            </CustomDialogTitle>
            <DialogContent>
                {isLoading ?
                    <SkeletonLoading formLoading FieldAmount={6} width={"100%"} />
                    :
                    <>
                        <Typography color="secondary" variant='body2' mb={1}>
                            Please click on a document to download it.
                        </Typography>
                        <Grid container>
                            <List sx={{ width: '100%' }}>{
                                documentsUploaded.map((doc, index) => {
                                    const fileNameWithPrefix = doc.fullNameAndType;
                                    const firstHyphenIndex = fileNameWithPrefix.indexOf('-');
                                    const lastPeriodIndex = fileNameWithPrefix.lastIndexOf('.');

                                    const fileNameWithoutPrefixAndExtension = fileNameWithPrefix.substring(firstHyphenIndex + 1, lastPeriodIndex).trim();
                                    const fileExtension = doc.documentName.split('.').pop() || '';
                                    const extensionInfo = getIconAndNameFromExtension(fileExtension);
                                    const icon = extensionInfo ? extensionInfo[0] : null;
                                    const extensionName = extensionInfo ? extensionInfo[1] : null;

                                    return (
                                        <ListItem key={index} sx={{ borderBottom: 1, borderColor: 'divider', padding: 1, width: '100%' }}>
                                            <ListItemAvatar>
                                                {icon ? (
                                                    <img src={`${process.env.PUBLIC_URL}/icons/fileTypes/${icon}`} alt={`${doc.documentName} icon`} width="50px" height="50px" />
                                                ) : (
                                                    <Avatar>
                                                        <Folder />
                                                    </Avatar>
                                                )}
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={<span style={{ fontSize: '1rem' }}>{fileNameWithoutPrefixAndExtension}</span>}
                                                secondary={<span style={{ fontSize: '0.875rem' }}>{extensionName} &bull; {formatFileSize(doc.fileSize)}</span>}
                                            />
                                            <IconButton edge="end" aria-label="download" onClick={() => getDocument(doc.documentId)}>
                                                <Download />
                                            </IconButton>
                                        </ListItem>
                                    );
                                })
                            }
                            </List>
                        </Grid>
                    </>
                }
            </DialogContent>
        </Dialog>
    )
}